import { useEffect, useMemo, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchWallet } from 'redux/actions/walletActions';
import { getCurrencyCodeForCountry, getPriceByDate } from 'utils/utils';

const dlog = (...args) => {
    if (typeof process !== 'undefined' && process.env?.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.log(...args);
    }
};

/* Transaction status options for the filter chips */
export const TRANSACTION_STATUSES = [
    { id: 'all',      label: 'All',       value: '' },
    { id: 'pending',  label: 'Pending',   value: 'pending' },
    { id: 'reversed', label: 'Declined',  value: 'reversed' },
    { id: 'paid',     label: 'Completed', value: 'paid' },
];

/**
 * useWallet — centralized data hook for the Wallet/Transactions page.
 *
 * Returns:
 *   - Wallet balances & stat cards (parsed from Redux)
 *   - Filtered transaction history
 *   - Filter state + setters (status, reference, date)
 *   - Currency rates (parallel fetch)
 *   - Loading flags
 *
 * Does NOT return JSX. Pure data + handlers.
 */
const useWallet = () => {
    const dispatch = useDispatch();

    const { wallet, loading: walletLoading, error: walletError } = useSelector(
        (s) => s.walletReducer ?? {}
    );
    const userLocation = useSelector((s) => s.userLocationReducer);

    const currencySymbol = getCurrencyCodeForCountry(
        userLocation?.location?.country_code ?? 'NG'
    );

    /* Filters */
    const [statusFilter, setStatusFilter]       = useState('all');
    const [referenceFilter, setReferenceFilter] = useState('');
    const [dateFilter, setDateFilter]           = useState('');

    /* Currency rates */
    const [currencyRates, setCurrencyRates]   = useState({});
    const [loadingRates, setLoadingRates]     = useState(false);

    /* ── Initial fetch ────────────────────────────────────── */
    useEffect(() => {
        dispatch(fetchWallet());
    }, [dispatch]);

    const refresh = useCallback(() => {
        dispatch(fetchWallet());
    }, [dispatch]);

    /* ── Currency rates (parallel) ─────────────────────────── */
    useEffect(() => {
        const histories = wallet?.histories;
        if (!histories?.length) return;

        let cancelled = false;
        const uniqueDates = [...new Set(histories.map((h) => h.updatedAt).filter(Boolean))];
        if (uniqueDates.length === 0) return;

        setLoadingRates(true);
        Promise.all(
            uniqueDates.map(async (date) => {
                try {
                    const rate = await getPriceByDate(date, currencySymbol);
                    return [date, rate];
                } catch (err) {
                    dlog('[useWallet] rate fetch failed', date, err);
                    return [date, null];
                }
            })
        )
            .then((results) => {
                if (cancelled) return;
                const next = {};
                results.forEach(([d, r]) => { next[d] = r; });
                setCurrencyRates(next);
            })
            .finally(() => {
                if (!cancelled) setLoadingRates(false);
            });

        return () => { cancelled = true; };
    }, [wallet?.histories, currencySymbol]);

    /* ── Stat cards (derived from wallet data) ─────────────── */
    const statCards = useMemo(() => {
        return [
            {
                id: 'balance',
                name: 'Wallet Balance',
                primaryLabel: 'Available for payout',
                primaryValue: wallet?.ledger_balance ?? 0,
                secondaryLabel: 'Total balance',
                secondaryValue: wallet?.actual_balance ?? 0,
                symbol: '₦',
                accent: 'navy',
            },
            {
                id: 'commission',
                name: 'Commission',
                primaryLabel: 'Completed',
                primaryValue: wallet?.lifetime_commision_verified ?? 0,
                secondaryLabel: 'Total earned',
                secondaryValue: wallet?.lifetime_commision ?? 0,
                symbol: '₦',
                accent: 'success',
            },
            {
                id: 'sales',
                name: 'Lifetime Sales',
                primaryLabel: 'Completed sales',
                primaryValue: wallet?.lifetime_sales_value_verified ?? 0,
                secondaryLabel: 'Total sales',
                secondaryValue: wallet?.lifetime_sales_value ?? 0,
                symbol: '₦',
                accent: 'info',
            },
            {
                id: 'payouts',
                name: 'Payouts',
                primaryLabel: 'Successful',
                primaryValue: wallet?.lifetime_payout_verified ?? 0,
                secondaryLabel: 'Total requested',
                secondaryValue: wallet?.lifetime_payout ?? 0,
                symbol: '₦',
                accent: 'purple',
            },
        ];
    }, [wallet]);

    /* ── Apply filters to history ──────────────────────────── */
    const filteredHistory = useMemo(() => {
        const histories = wallet?.histories ?? [];
        if (!histories.length) return [];

        const ref = referenceFilter.trim().toLowerCase();
        const dateMs = dateFilter ? new Date(dateFilter).toDateString() : null;

        return histories.filter((item) => {
            /* Status filter */
            if (statusFilter !== 'all') {
                const itemStatus = (item.status ?? '').toLowerCase();
                if (itemStatus !== statusFilter) return false;
            }

            /* Reference / order number search */
            if (ref) {
                const tx = String(item.transaction_id ?? '').toLowerCase();
                const order = String(item.order_number ?? '').toLowerCase();
                if (!tx.includes(ref) && !order.includes(ref)) return false;
            }

            /* Date filter */
            if (dateMs && item.createdAt) {
                if (new Date(item.createdAt).toDateString() !== dateMs) return false;
            }

            return true;
        });
    }, [wallet?.histories, statusFilter, referenceFilter, dateFilter]);

    /* ── Active filter pill data ───────────────────────────── */
    const activeFilters = useMemo(() => {
        const list = [];
        if (statusFilter !== 'all') {
            const meta = TRANSACTION_STATUSES.find((s) => s.value === statusFilter);
            list.push({ key: 'status', label: 'Status', value: meta?.label ?? statusFilter });
        }
        if (referenceFilter) list.push({ key: 'reference', label: 'Reference', value: `"${referenceFilter}"` });
        if (dateFilter)      list.push({ key: 'date',      label: 'Date',      value: dateFilter });
        return list;
    }, [statusFilter, referenceFilter, dateFilter]);

    const hasActiveFilters = activeFilters.length > 0;

    /* ── Filter clearing ───────────────────────────────────── */
    const clearFilter = useCallback((key) => {
        if (key === 'status')    setStatusFilter('all');
        if (key === 'reference') setReferenceFilter('');
        if (key === 'date')      setDateFilter('');
    }, []);

    const clearAllFilters = useCallback(() => {
        setStatusFilter('all');
        setReferenceFilter('');
        setDateFilter('');
    }, []);

    return {
        /* Data */
        wallet,
        statCards,
        history: filteredHistory,
        totalHistoryCount: wallet?.histories?.length ?? 0,
        filteredHistoryCount: filteredHistory.length,
        loading: walletLoading,
        loadingRates,
        error: walletError,
        currencyRates,
        currencySymbol,

        /* Filters */
        statusFilter, setStatusFilter,
        referenceFilter, setReferenceFilter,
        dateFilter, setDateFilter,
        activeFilters,
        hasActiveFilters,
        clearFilter,
        clearAllFilters,

        /* Actions */
        refresh,
    };
};

export default useWallet;